import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
export function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: LegalModalProps) {
  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-83">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-84">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-[#0a0f1a]/80 backdrop-blur-sm" data-id="element-85" />

          {/* Modal Card */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-2xl max-h-[85vh] flex flex-col bg-[#0f1520] border border-white/10 rounded-lg shadow-2xl overflow-hidden" data-id="element-86">
            {/* Top Accent Line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-600 to-teal-400" data-id="element-87" />

            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/5" data-id="element-88">
              <h2 className="text-xl font-bold text-white tracking-wide uppercase" data-id="element-89">
                {title}
              </h2>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-md transition-colors" aria-label="Close modal" data-id="element-90">
                <X className="w-5 h-5" data-id="element-91" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 overflow-y-auto custom-scrollbar text-gray-300 space-y-6 font-sans leading-relaxed" data-id="element-92">
              {children}
            </div>

            {/* Bottom Fade out for scroll indication */}
            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#0f1520] to-transparent pointer-events-none" data-id="element-93" />
          </motion.div>
        </div>}
    </AnimatePresence>;
}